'use client';

import React from 'react';
import { Container } from '@/components/utils/Container';
import { Button } from '@/components/ui/forms/button';
import { motion } from 'framer-motion';
import {
  UserGroupIcon,
  HeartIcon,
  RocketLaunchIcon,
  SparklesIcon,
  GlobeAltIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import Link from 'next/link';
import { PublicPageHero, PublicPageCTA } from '@/components/features/public';
import { SectionHeader } from '@/components/ui/design-system';

const timelineEvents = [
  {
    year: '2012',
    title: 'From engineering to recruiting',
    description:
      'Kevin launched a contingent recruiting firm after running manufacturing plants and managing large teams.',
  },
  {
    year: '2015',
    title: 'The pattern emerges',
    description:
      'For every candidate placed, dozens more talented people remained stuck in the search. The system was broken.',
  },
  {
    year: '2018',
    title: 'Building at scale',
    description:
      'Developed a job marketing platform sending hundreds of millions of emails monthly, matching millions of candidates to roles.',
  },
  {
    year: '2024',
    title: 'CareerOffensive is born',
    description:
      'Built to help people go on offense — identify the next step up, find the right companies, and reach decision-makers directly.',
  },
];

const expertise = [
  {
    icon: ChartBarIcon,
    title: 'Job board architecture',
    description:
      'Built and scaled platforms that match millions of candidates to opportunities.',
  },
  {
    icon: UserGroupIcon,
    title: 'Recruiting operations',
    description:
      'Ran contingent recruiting and consulted for major hiring organizations.',
  },
  {
    icon: GlobeAltIcon,
    title: 'Distributed leadership',
    description:
      'Led engineering teams across borders through extraordinary circumstances.',
  },
];

const brokenSystem = [
  {
    title: 'Online applications became black holes',
    description: 'Applications disappeared into overloaded ATS systems.',
  },
  {
    title: '"Easy Apply" became "Apply to everything"',
    description: 'AI-driven auto-applies flooded postings with low-intent resumes.',
  },
  {
    title: 'Great candidates got buried in noise',
    description: 'Competing against automated volume, not other humans.',
  },
];

export default function AboutClient() {
  return (
    <div className="min-h-screen bg-background pt-28">
      <PublicPageHero
        eyebrow="Founder story"
        headline="About"
        accent="Kevin Holbert"
        description="Founder, CareerOffensive."
      />

      <Container className="pb-16 pt-12 max-w-5xl space-y-16">
        {/* Origin Story */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="bg-card border border-border border-t border-t-primary p-6 sm:p-8"
          style={{ boxShadow: 'var(--shadow-warm-sm)' }}
        >
          <div className="space-y-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
            <p>
              <strong className="text-foreground font-semibold">
                Kevin Holbert grew up in a tiny village in Ohio called East Palestine
              </strong>{' '}
              — a place almost no one had heard of until a train derailment put it on the map for
              all the wrong reasons in 2022. Coming from a small town shaped how he works:{' '}
              <strong className="text-foreground">
                straight talk, loyalty, and a deep sense of responsibility
              </strong>{' '}
              to the people who trust him.
            </p>
            <p>
              Before he ever built a job board or a career product, Kevin was an{' '}
              <strong className="text-foreground">engineer and operator</strong>. He ran
              manufacturing plants, managed large teams, and learned firsthand how companies make
              hiring decisions, what they really look for in people, and how much talent gets
              missed simply because the system is noisy and inefficient.
            </p>
          </div>
        </motion.section>

        {/* Timeline */}
        <section>
          <SectionHeader
            level="page"
            title="The journey"
            description="Twelve years from the plant floor to a product built to fix what's broken."
            className="mb-6"
          />
          <div className="grid gap-3 sm:grid-cols-2">
            {timelineEvents.map((event, index) => (
              <motion.div
                key={event.year}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.25, delay: 0.05 + index * 0.04, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="bg-card border border-border border-t border-t-primary p-4 sm:p-5"
                style={{ boxShadow: 'var(--shadow-warm-sm)' }}
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center bg-primary text-primary-foreground text-sm font-bold tabular-nums">
                    {event.year}
                  </div>
                  <div className="space-y-1 min-w-0">
                    <h3 className="text-sm sm:text-base font-bold text-foreground leading-snug">
                      {event.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {event.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Broken System */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="bg-card border border-primary border-t border-t-primary p-6 sm:p-8"
          style={{ boxShadow: 'var(--shadow-warm-sm)' }}
        >
          <SectionHeader
            level="page"
            title="The system is broken"
            description="From the outside it looked like a success story. From the inside, Kevin could see the cracks."
            className="mb-6"
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {brokenSystem.map((item) => (
              <div key={item.title} className="bg-background/60 p-4 border border-border">
                <p className="text-sm font-semibold text-foreground leading-snug">{item.title}</p>
                <p className="mt-1.5 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-base sm:text-lg font-semibold text-foreground text-center">
            The traditional online job search is no longer efficient.
          </p>
        </motion.section>

        {/* Expertise */}
        <section>
          <SectionHeader
            level="page"
            title="15+ years of expertise"
            description="Kevin has lived every side of the hiring equation."
            className="mb-6"
          />
          <div className="grid gap-3 sm:grid-cols-3">
            {expertise.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.25, delay: 0.05 + index * 0.04, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="bg-card border border-border border-t border-t-primary p-5 transition-transform duration-100 hover:-translate-y-px"
                style={{ boxShadow: 'var(--shadow-warm-sm)' }}
              >
                <div className="flex h-10 w-10 items-center justify-center bg-primary/10 border border-primary/30 mb-3">
                  <item.icon className="w-5 h-5 text-primary stroke-[2px]" />
                </div>
                <h3 className="text-sm sm:text-base font-bold text-foreground leading-snug">
                  {item.title}
                </h3>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Leadership Story */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="bg-card border border-border border-t border-t-primary p-6 sm:p-8"
          style={{ boxShadow: 'var(--shadow-warm-sm)' }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center bg-primary/10 border border-primary/30">
              <HeartIcon className="w-5 h-5 text-primary stroke-[2px]" />
            </div>
            <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight text-foreground">
              Leadership that shows up
            </h2>
          </div>
          <div className="space-y-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
            <p>
              Kevin's approach to business isn't just technical —{' '}
              <strong className="text-foreground">it's deeply personal</strong>.
            </p>
            <p>
              For nearly a decade, he has led a distributed engineering and data team based in
              Ukraine. When the war began and borders closed, one of his senior developers and
              that developer's young family were suddenly living in an active war zone.
            </p>
            <p>
              Instead of sending an email and hoping for the best,{' '}
              <strong className="text-foreground">
                Kevin flew to Eastern Europe, crossed into Ukraine himself with a security team,
                and worked on the ground to help his employee's family reach safety
              </strong>
              . It was risky, uncomfortable, and far outside the normal job description of a
              founder — and exactly in line with how he believes leaders should act.
            </p>
            <blockquote className="bg-primary/8 border-l-[3px] border-primary p-4 sm:p-5">
              <p className="text-foreground font-semibold text-base sm:text-lg italic">
                &quot;You don't leave people stranded in a broken system when you have the means to
                help.&quot;
              </p>
            </blockquote>
          </div>
        </motion.section>

        {/* Why CareerOffensive */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="bg-card border border-border border-t border-t-primary p-6 sm:p-8"
          style={{ boxShadow: 'var(--shadow-warm-sm)' }}
        >
          <SectionHeader
            level="page"
            title="Why CareerOffensive exists"
            className="mb-5"
          />
          <div className="space-y-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
            <p>
              Instead of asking, &quot;How do we help people apply to more jobs?&quot;,
              CareerOffensive is built around a different question:
            </p>
            <div className="bg-primary/8 border border-primary border-t border-t-primary p-5 sm:p-6 text-center">
              <p className="text-base sm:text-lg font-semibold text-foreground leading-snug">
                How do we help people go on offense in their career — identify the next step up,
                find the right companies, and reach decision-makers directly?
              </p>
            </div>
            <p>
              CareerOffensive is the culmination of Kevin's experience building job boards,
              running massive job alert systems, consulting for hiring organizations, and living
              every side of the hiring equation:{' '}
              <strong className="text-foreground">
                candidate, recruiter, platform owner, and employer
              </strong>
              .
            </p>
            <p>
              He knows exactly what happens after you click &quot;Apply&quot; — and why you so
              often never hear back.
            </p>
          </div>
        </motion.section>

        {/* Final CTA */}
        <PublicPageCTA
          title="Ready to go on the offensive?"
          description="If you're tired of playing defense in your job search, CareerOffensive is the playbook Kevin wishes every job seeker had years ago."
          actions={
            <>
              <Button asChild size="lg" className="px-8">
                <Link href="/get-started">
                  <RocketLaunchIcon className="w-4 h-4" />
                  Start your offensive
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="px-8">
                <Link href="/how-it-works">
                  How it works
                  <SparklesIcon className="w-4 h-4" />
                </Link>
              </Button>
            </>
          }
        />
      </Container>
    </div>
  );
}
